/**
 * Single source of truth for FAQ content.
 * Used by:
 *  - Home.jsx (shows a progressively-revealed subset in the homepage accordion)
 *  - Faq.jsx  (shows the full list on the dedicated /faq page)
 *
 * Both pages build their FAQPage JSON-LD from this same array, so the
 * structured data always matches what's actually published — just add
 * new items here and both places update automatically.
 *
 * NOTE: several of the answers below (marked "VERIFY") were drafted from
 * plan copy in Pricing.jsx / llms.txt. Please review wording for accuracy
 * before publishing, especially anything about limits, reconnects, or
 * refund timelines that isn't literally quoted from your own product copy.
 */

export const faqs = [
  {
    q: "Do I need a website to sell with Checkout DM?",
    a: "Nope. Your Instagram profile is your storefront, and you also get a shareable public catalog page — no separate website needed. We generate secure checkout links right inside DMs.",
  },
  {
  q: "Can customers browse my products outside of DMs?",
  a: "Yes. Every paid merchant gets a shareable public catalog page customers can browse before messaging you — DMs handle the actual checkout.",
  },
  {
    q: "Which payment methods are supported?",
    a: "Every plan can accept payments manually via UPI, QR code, or bank transfer — no gateway required. For automatic payment confirmation, connect one gateway — Razorpay, Stripe, or PayPal — to accept cards, UPI, and wallets depending on which you enable.",
  },
  {
    q: "Do you support Reels comments and Story replies?",
    a: "Yes. Comment-to-DM automation is available from the Creator plan onward. Story replies are generic on Free, Creator, and Starter, and product-matched on Growth and Pro.",
  },
  {
    q: "Is Instagram OAuth required?",
    a: "Yes — Checkout DM is Instagram-exclusive today. Merchants connect via Instagram Business Login (OAuth).",
  },
  {
    q: "How much does Checkout DM cost?",
    a: "There are 5 plans: Free (₹0 / $0), Creator (₹499 / $9 per month), Starter (₹1,499 / $19), Growth (₹2,899 / $39) and Pro (₹6,499 / $69). Yearly billing gives 2 months free — you pay for 10 months. There are no transaction fees on any plan. Full details are on the Pricing page.",
  },
  {
    q: "Can I try it for free?",
    a: "Absolutely. The FREE tier lets you process a limited number of conversations every month with all core features.",
  },
  {
    q: "Does Checkout DM take a commission on my sales?",
    a: "No. Checkout DM never takes a commission or transaction fee on your orders — payments go straight to your own bank account.",
  },
  {
    q: "Who actually receives my customers' payments?",
    a: "Payments always go directly to your own connected payment account (Razorpay, UPI, or bank transfer) — Checkout DM never holds your money.",
  },
  {
    q: "Can I upgrade or downgrade my plan later?",
    a: "Yes, anytime. You can move between Free, Creator, Starter, Growth, and Pro without losing your products, orders, or customer history.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. There are no lock-in contracts on any plan — you can cancel anytime.",
  },
  {
    q: "How many products can I list on the Free plan?",
    a: "The Free plan includes up to 5 products. Creator includes 150, Starter raises this to 500, Growth to 1,000, and Pro is 10,000.",
  },
  {
    q: "How many customer conversations does the Free plan include?",
    a: "The Free plan includes 15 unique customer conversations per month. Creator and all higher plans include unlimited conversations.",
  },
  {
    q: "Will Checkout DM branding be shown to my customers?",
    a: "On the Free plan, yes — customers see Checkout DM branding in DM replies, checkout, and emails. Creator removes it from DMs but keeps it in emails and on the storefront. Starter, Growth, and Pro remove Checkout DM branding everywhere.",
  },
  {
    q: "Can I sell digital products, not just physical ones?",
    a: "Yes. Digital products are delivered automatically to the buyer via a secure link right after payment is confirmed. The Creator plan is digital-only; every other plan can sell both physical and digital products.",
  },
  {
  q: "Can I offer discount codes or coupons?",
  a: "Yes, on Growth and Pro plans. Set up discount codes from your dashboard, and customers can apply them right in the DM checkout flow.",
  },
  {
    q: "How do returns and refunds work?",
    a: "Buyers can request a return directly in DM, and you manage and approve returns from your merchant dashboard. Merchants can also log the return manually from their dashboard.",
  },
  {
    q: "Do I need to be technical or know how to code to set this up?",
    a: "Not at all. Connecting your Instagram account and setting up your catalog takes a few clicks from the merchant dashboard — no code required.",
  },
  {
    q: "Can my team help manage the store with me?",
    a: "Yes, on the Pro plan. You get 1 admin (your connected Instagram account) plus 2 included team member seats, and you can add more seats at any time for a small per-seat monthly fee.",
  },
  {
    q: "Do I need a Razorpay account to get started?",
    a: "No. You can accept payments manually via UPI ID, QR code, or bank transfer without any gateway. If you want payments confirmed automatically, connect Razorpay, Stripe, or PayPal — you can enable one gateway at a time, whichever fits your business best.",
  },
  {
    q: "What happens when I accept customer payments via QR code or UPI?",
    a: "As a merchant when you accept customer payments via UPI id, QR code, or bank transfer, these won't be auto verified. You will be manually accepting such payment from your merchant dashboard.",
  },
  {
  q: "Can customers browse products by category instead of searching?",
  a: "Yes. Customers can browse your catalog by category directly in DM, or search by product name — whichever's faster for them.",
  },
  {
  q: "Do you handle shipping and delivery tracking?",
  a: "Yes. Connect a supported shipping partner (like Shiprocket or Shippo) from your dashboard, and orders are auto-shipped after payment. Starter, Growth, and Pro plans also get automatic delivery-status tracking. (Creator is digital-only, so shipping doesn't apply.)",
  },
  {
    q: "Is customer payment data secure?",
    a: "Yes. Checkout DM uses PCI-safe payment links, signed URLs, and OAuth-based merchant authentication — customer card details are never stored on our side.",
  },
  {
    q: "What kind of support do I get?",
    a: "Every plan includes email support. Creator and Starter get faster email response times, Growth gets priority support, and Pro gets dedicated support.",
  },
  {
    q: "Can I see analytics and track my orders in one place?",
    a: "Yes. Paid plans include an order dashboard, and Growth and Pro add analytics like revenue, response time, and conversion tracking.",
  },
  {
    q: "What happens if I disconnect my Instagram account?",
    a: "Your catalog, order history, and settings stay saved — you can reconnect the same Instagram account at any time to resume where you left off.",
  },
  {
    q: "What happens if I go over my monthly conversation limit on the Free plan?",
    a: "You'll be prompted to upgrade to a paid plan to continue handling new conversations that month.",
  },
  {
    q: "Can international customers buy from my store?",
    a: "Yes. Alongside manual UPI/bank transfer, you can connect Stripe, PayPal, or Razorpay to accept international card payments — you can have one automatic gateway active at a time, so pick whichever fits your customer base best.",
  },
  {
    q: "How is Checkout DM different from just sharing a UPI QR code in DMs?",
    a: "A QR code alone doesn't confirm payment automatically or track orders. Checkout DM lets you verify payment, updates order status, and lets you manage everything from one dashboard.",
  },
  {
    q: "Can I check the status of all customers who have added to the cart but haven't yet paid?",
    a: "Yes. On every plan, the Chats tab shows each customer's cart, quantity and current stage. Starter and above also get an abandoned-cart alert in the Stuck tab, and Growth and Pro automatically send the customer reminders. If a customer tells the bot why they hesitated (for example, asking for a discount), that reason is shown too — in full on Growth and Pro, and for a limited number of carts on Starter.",
  },
  {
    q: "Will my customer also get email confirmation on order placement?",
    a: "Yes. As soon as the payment confirmed is received, order received email is sent to the customer. Similarly, whenever return request is approved by merchant, the customer gets an email confirmation.",
  },
  {
    q: "What happens when a customer wants merchant's attention?",
    a: "As a merchant, you can clearly see that status from your dashboard and help the customer in case needed.",
  },
];
